import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import '../styles/global.css';

export default function Model3DViewer({ modelUrl, fullScreen = false, onClose }) {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!containerRef.current || !modelUrl) return;

    const initScene = async () => {
      try {
        setLoading(true);
        setError('');

        // Scene setup
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf0f0f0);
        sceneRef.current = scene;

        // Camera setup
        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;
        const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
        camera.position.set(0, 2, 5);
        cameraRef.current = camera;

        // Renderer setup
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(width, height);
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.shadowMap.enabled = true;
        containerRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // Lighting
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
        scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(5, 10, 5);
        directionalLight.castShadow = true;
        directionalLight.shadow.mapSize.width = 2048;
        directionalLight.shadow.mapSize.height = 2048;
        scene.add(directionalLight);

        // Load model
        const loader = new GLTFLoader();
        loader.load(
          modelUrl,
          (gltf) => {
            const model = gltf.scene;
            model.scale.set(1, 1, 1);
            scene.add(model);

            // Auto-fit camera to model
            const box = new THREE.Box3().setFromObject(model);
            const center = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());

            const maxDim = Math.max(size.x, size.y, size.z);
            const fov = camera.fov * (Math.PI / 180);
            let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));

            cameraZ *= 1.5;

            camera.position.copy(center);
            camera.position.z += cameraZ;
            camera.lookAt(center);

            // Orbit controls
            const controls = new OrbitControls(camera, renderer.domElement);
            controls.target.copy(center);
            controls.autoRotate = true;
            controls.autoRotateSpeed = 4;
            controls.enableDamping = true;
            controls.dampingFactor = 0.05;
            controls.enableZoom = true;
            controls.enablePan = true;
            controls.update();
            controlsRef.current = controls;

            setLoading(false);
          },
          undefined,
          (error) => {
            console.error('Model loading error:', error);
            setError('Failed to load 3D model');
            setLoading(false);
          }
        );

        // Animation loop
        const animate = () => {
          requestAnimationFrame(animate);

          if (controlsRef.current) {
            controlsRef.current.update();
          }

          renderer.render(scene, camera);
        };

        animate();

        // Handle window resize
        const handleResize = () => {
          if (!containerRef.current) return;
          const newWidth = containerRef.current.clientWidth;
          const newHeight = containerRef.current.clientHeight;

          camera.aspect = newWidth / newHeight;
          camera.updateProjectionMatrix();
          renderer.setSize(newWidth, newHeight);
        };

        window.addEventListener('resize', handleResize);

        return () => {
          window.removeEventListener('resize', handleResize);
          if (containerRef.current && renderer.domElement) {
            containerRef.current.removeChild(renderer.domElement);
          }
          renderer.dispose();
        };
      } catch (err) {
        console.error('Scene initialization error:', err);
        setError('Error initializing 3D viewer');
        setLoading(false);
      }
    };

    initScene();
  }, [modelUrl]);

  // useEffect(() => {
  //   const handleFullscreenChange = () => {
  //     const isActive =
  //       document.fullscreenElement ||
  //       document.webkitFullscreenElement ||
  //       document.mozFullScreenElement ||
  //       document.msFullscreenElement;
  //     // setIsFullscreen(!!isActive);
  //   };

  //   document.addEventListener('fullscreenchange', handleFullscreenChange);
  //   document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
  //   document.addEventListener('mozfullscreenchange', handleFullscreenChange);
  //   document.addEventListener('MSFullscreenChange', handleFullscreenChange);

  //   return () => {
  //     document.removeEventListener('fullscreenchange', handleFullscreenChange);
  //     document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
  //     document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
  //     document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
  //   };
  // }, []);

  // const toggleFullscreen = async () => {
  //   const container = containerRef.current;
  //   if (!container) return;

  //   const isCurrentlyFullscreen =
  //     document.fullscreenElement ||
  //     document.webkitFullscreenElement ||
  //     document.mozFullScreenElement ||
  //     document.msFullscreenElement;

  //   if (!isCurrentlyFullscreen) {
  //     const requestFullscreen =
  //       container.requestFullscreen ||
  //       container.mozRequestFullScreen ||
  //       container.webkitRequestFullscreen ||
  //       container.msRequestFullscreen;

  //     if (requestFullscreen) {
  //       await requestFullscreen.call(container);
  //     }
  //     setIsFullscreen(true);
  //   } else {
  //     const exitFullscreen =
  //       document.exitFullscreen ||
  //       document.mozCancelFullScreen ||
  //       document.webkitExitFullscreen ||
  //       document.msExitFullscreen;

  //     if (exitFullscreen) {
  //       await exitFullscreen.call(document);
  //     }
  //     setIsFullscreen(false);
  //   }
  // };

  // const handleRecenterView = () => {
  //   if (sceneRef.current && cameraRef.current && controlsRef.current) {
  //     const box = new THREE.Box3().setFromObject(sceneRef.current);
  //     const center = box.getCenter(new THREE.Vector3());
  //     const size = box.getSize(new THREE.Vector3());

  //     const maxDim = Math.max(size.x, size.y, size.z);
  //     const fov = cameraRef.current.fov * (Math.PI / 180);
  //     let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));

  //     cameraZ *= 1.5;

  //     cameraRef.current.position.copy(center);
  //     cameraRef.current.position.z += cameraZ;
  //     cameraRef.current.lookAt(center);

  //     controlsRef.current.target.copy(center);
  //     controlsRef.current.update();
  //   }
  // };

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: fullScreen ? '100vh' : '500px',
        position: 'relative',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: 'var(--shadow)',
      }}
    >
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            textAlign: 'center',
          }}
        >
          <div className="loader"></div>
          <p style={{ marginTop: '1rem', color: '#555' }}>Loading 3D Model...</p>
        </div>
      )}

      {error && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            textAlign: 'center',
            backgroundColor: '#ffe5e5',
            padding: '2rem',
            borderRadius: '8px',
            border: '2px solid var(--primary-color)',
          }}
        >
          <p style={{ color: '#c0392b', fontWeight: 'bold' }}>⚠️ {error}</p>
        </div>
      )}

      {/* Controls bar */}
      <div
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 20,
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >



        {onClose && (
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#ee8230',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = '#7f8c8d')}
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = '#95a5a6')
            }
          >
            ✕ Close
          </button>
        )}
      </div>

    </div>
  );
}
